import { ContactState, GroupStatusEvent, isBotPServed, PServed } from '@wui/im'
import dayjs from 'dayjs'
import throttle from 'lodash.throttle'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'


import { useStoreGroups } from '@/entities/contact-group'
import { useChatMessageStore } from '@/entities/messages'

import { useApi } from '@/shared/composables/api'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { usePaginatedData } from '@/shared/composables/use-paginated-data'
import { emitter } from '@/shared/services/emitter'
import { Contact, PostContactsBody, GetContactsQuery } from '@/shared/types/api'
import { EmitterEvents } from '@/shared/types/emitter-events'
import { GroupsTypes } from '@/shared/types/groups'
import { OwnStatus } from '@/shared/types/store'
import { PSTNGUEST } from '@/shared/utils/constants'
import {
  findMatchingSystemBotContacts,
  getSystemBotContact,
  hasRealBotName,
  mergeSystemBotSearchResults,
  resolveBotContact,
  resolveBotDisplayName,
} from '@/shared/utils/contacts'
import { applyBotIdentityToRooms, createPrivateRoom } from '@/shared/utils/im'
import sort from '@/shared/utils/sort'
import { removePartStringFromString } from '@/shared/utils/string'
import { updateStatusIcon } from '@/shared/utils/tauri'
import { fromPServedUser, isPServedIsUser, pServedIsRoom } from '@/shared/utils/uri'

import { useStoreContactsFilterable } from '..'

const { byName } = sort.contacts

export const useStoreContacts = defineStore('contacts', () => {
  const { currentUser } = useCurrentUser()
  const contactStoreIsReady = ref(false)
  const listForGuestRoom = ref<Map<string, Contact>>(new Map())
  const selectedGroup = ref<GroupsTypes>(GroupsTypes.ALL)
  const ownStatus = ref<OwnStatus>(OwnStatus.DEFAULT)
  const ownStatusMessage = ref('')
  const pServedsToFetch = ref(new Set<string>())
  const internalNumbersToFetch = ref(new Set<string>())
  const botPServedsToFetch = ref(new Set<string>())
  const botPServedsFetching = new Set<string>()
  const botPServedsResolved = new Set<string>()
  const alreadyFetchedMonths = new Set<string>()
  const fetchingIds = new Set<string>()
  const contacts = ref<Contact[]>([])
  const pendedContactStatuses = ref<Map<PServed, GroupStatusEvent>>(new Map())
  const contactsUserAgents = ref<Map<PServed, (string | null)[]>>(new Map())

  const favoriteContacts = computed<Array<Contact>>(() => allContacts.value.filter(({ isFavorite }) => !!isFavorite))

  const { list, fetchNext, reset, total, isLoading } = usePaginatedData<Contact>({
    size: 50,
    idKey: 'guid',
    fetchData: (data: GetContactsQuery) => useApi().getContacts(data).then((data) =>{
      contactStoreIsReady.value = true
      return data
    }),
  })

  const allContacts = computed(() => {
    const all = [...list.value, ...contacts.value]

    const ids = new Set()
    return all.filter(contact => {
      if (!ids.has(contact.id)) {
        ids.add(contact.id)

        return true
      } else {
        return false
      }
    }).sort(byName)
  })

  const alphabeticallySortedNotFavoriteContacts = computed(() => {
    return list.value.filter(({ isFavorite }) => !isFavorite).sort(byName)
  })

  const alphabeticallySortedFavoritesContacts = computed(() => {
    return allContacts.value.filter(({ isFavorite }) => isFavorite).sort(byName)
  })

  const alphabeticallySortedContacts = computed(() => {
    return [...alphabeticallySortedFavoritesContacts.value, ...alphabeticallySortedNotFavoriteContacts.value]
  })


  const internalNumberContactMap = computed<{ [internalNumber: string]: Contact }>(() => {
    return allContacts.value.reduce((result, contact) => ({
      ...result,
      [contact.internalNumber]: contact,
    }), {})
  })

  const externalContactMobilePhoneMap = computed<{ [mobilePhone: string]: Contact }>(() => {
    return allContacts.value
      .filter(contact => typeof contact.mobilePhone === 'string' && contact.mobilePhone.length > 0)
      .reduce((result, contact) => {
        result[contact.mobilePhone as string] = contact
        return result
      }, {} as { [mobilePhone: string]: Contact })
  })

  const setContactIfDoesNotExists = (contact: Contact) => {
    if (!contacts.value.find(c => (c.id === contact.id))) {
      contacts.value.push(contact)
    }
  }

  const upsertBotContact = (contact: Contact): Contact => {
    const isSameBot = (item: Contact) => item.id === contact.id || (!!contact.imLogin && item.imLogin === contact.imLogin)
    const existingExtra = contacts.value.find(isSameBot)
    if (existingExtra) {
      if (hasRealBotName(contact.name, contact.imLogin) && existingExtra.name !== contact.name) {
        existingExtra.name = contact.name
      }
      return existingExtra
    }

    const existingList = list.value.find(isSameBot)
    if (existingList) {
      if (hasRealBotName(contact.name, contact.imLogin) && existingList.name !== contact.name) {
        existingList.name = contact.name
      }
      return existingList
    }

    contacts.value.push(contact)
    return contact
  }

  const queueBotNameFetch = (pServed: string) => {
    if (
      botPServedsFetching.has(pServed)
      || botPServedsToFetch.value.has(pServed)
      || botPServedsResolved.has(pServed)
    ) {
      return
    }

    const next = new Set(botPServedsToFetch.value)
    next.add(pServed)
    botPServedsToFetch.value = next
  }

  const applyResolvedBotIdentity = (pServed: string, name: string) => {
    if (!hasRealBotName(name, pServed)) {
      return
    }

    upsertBotContact(resolveBotContact(pServed, name))
    applyBotIdentityToRooms(pServed, name)
  }

  const isContactCanBeAddedToPersonal = (contact: Contact) => {
    return !contact.imLogin
      && !!(contact.mobilePhone || contact.internalNumber)
      && contact.type === 'pstnGuest'
      && !findContactByMobilePhone(contact.mobilePhone ?? '')
  }

  const findByInternalNumber = (internalNumber: string) => {
    const contact = internalNumberContactMap.value[internalNumber]
    if (contact) {
      return contact
    }
    internalNumbersToFetch.value.add(internalNumber)
    return
  }

  const findById = (id: string): Contact | undefined => {
    return allContacts.value.find(contact => contact.id === id)
  }

  const findContactByMobilePhone = (mobilePhone: string): Contact | undefined => {
    return externalContactMobilePhoneMap.value[mobilePhone] ?? listForGuestRoom.value.get(mobilePhone)
  }

  const findByPServed = (pServed: string): Contact | undefined => {
    if (!pServed) {
      return
    }

    const contact = allContacts.value.find((item) => item.imLogin === pServed) ?? listForGuestRoom.value.get(pServed)
    if (contact) {
      if (isBotPServed(pServed) && !hasRealBotName(contact.name, pServed)) {
        queueBotNameFetch(pServed)
      }
      return contact
    }

    const systemBot = getSystemBotContact(pServed)
    if (systemBot) {
      return systemBot
    }

    if (isBotPServed(pServed)) {
      const placeholder = upsertBotContact(resolveBotContact(pServed))
      queueBotNameFetch(pServed)
      return placeholder
    }

    if (pServed && isPServedIsUser(pServed) && pServed !== currentUser.value?.imLogin){
      pServedsToFetch.value.add(pServed)
    }
    return
  }

  const findGuestById = (id: string) => {
    return listForGuestRoom.value.get(id)
  }

  const findByGuid = (guid: string) => {
    return allContacts.value.find(contact => contact.guid === guid)
  }

  const setContact = (contact: Contact) => {
    for (let i = 0; i < list.value.length; i++) {
      if (list.value[i]?.id === contact.id) {
        list.value[i] = contact
        return
      }
    }
  }

  const setFavorite = (contact: Contact, isFavorite: boolean) => {
    return new Promise<void>((resolve, reject) => {
      const { addFavoriteContact, removeFavoriteContact } = useApi()
      const func = isFavorite ? addFavoriteContact : removeFavoriteContact
      func(contact.id)
        .then(() => {
          contact.isFavorite = isFavorite
          const { addOrUpdateContact } = useStoreContactsFilterable()
          addOrUpdateContact(contact)
          setContact(contact)
          resolve()
        })
        .catch((e) => reject(e))
    })
  }

  const addContactsForGuestRoom = (contactStatus: GroupStatusEvent) => {
    contactStatus.extendedParams?.contacts?.forEach((contact) => {
      const state = contact as ContactState
      if (!state.id.includes('GUEST')) {
        listForGuestRoom.value.set(state.id, {
          id: state.id,
          name: state.name,
          internalNumber: fromPServedUser(state.id),
          groupIds: [],
          organization: '',
          position: '',
          imLogin: state.id,
          terminalLogin: '',
          terminalPassword: '',
        })
      }
    })
  }

  const createExternalContact = (number: string, contactName?: string, type: ContactState['type'] = 'pstnGuest', contact?: Contact) => {
    const { t } = useLocalization()
    const externalContact: Contact = {
      type,
      name: (contact?.name ? contact.name : contactName ? contactName : `${removePartStringFromString(number, `${PSTNGUEST}-`)}`) + ` (${t('common.externalContact')})`,
      internalNumber: '',
      mobilePhone: contact?.mobilePhone ?? number,
      id: number,
      imLogin: '',
      isFavorite: false,
      groupIds: [],
      organization: contact?.organization ?? null,
      position: contact?.position ?? null,
      terminalLogin: '',
      terminalPassword: '',
      bDay: contact?.bDay ?? '',
    }
    return externalContact
  }

  const setSelectedGroup = (id: GroupsTypes) => {
    selectedGroup.value = id
  }

  const updateStatus = (contactStatus: GroupStatusEvent) => {
    const { pServed } = contactStatus
    if (currentUser.value?.imLogin === pServed) {
      ownStatus.value = contactStatus?.status
      ownStatusMessage.value = contactStatus?.message
      if (contactStatus.status === 0) {
        emitter.emit(EmitterEvents.UPDATE_STATUS)
      }
      updateStatusIcon(contactStatus?.status)
    } else {
      const contact = allContacts.value.find(x => x.imLogin === pServed)
      if (!contact) {
        const isGroup = pServedIsRoom(pServed)
        if (isGroup) {
          pendedContactStatuses.value.set(pServed, contactStatus)
        }
        return
      }

      const userAgents = contactStatus.extendedParams.userAgents
      if (userAgents) {
        contactsUserAgents.value.set(pServed, userAgents)
      } else if (contactsUserAgents.value.has(pServed)) {
        contactsUserAgents.value.delete(pServed)
      }
    }
  }

  const addUserContact = async (contact: Partial<PostContactsBody>) => {
    const data: PostContactsBody = {
      ...contact as PostContactsBody,
      contactType: 'externalContact',
    }

    const response = await useApi().addUserContact(data)
    if (response.status === 200 || response.status === 201) {
      list.value.push({
        ...response.data,
        id: response.data.guid || '',
        groupIds: [response.data?.groupId || ''],
      })

      return list.value.at(-1)
    } else {
      const { t } = useLocalization()
      throw new Error(t('mainAside.addContactError'))
    }
  }

  const editUserContact = async (contact: Contact): Promise<Contact | undefined> => {
    const response = await useApi().editUserContact(contact)
    if (response.status === 200) {
      const contactIndex = list.value.findIndex(c => c.guid === contact.guid)
      if (contactIndex === -1) return
      list.value[contactIndex] = response.data
      return list.value[contactIndex]
    } else {
      const { t } = useLocalization()
      throw new Error(t('mainAside.addContactError'))
    }
  }

  const removeUserContact = async (id: string, groupGuid: string) => {
    const response = await useApi().removeUserContact(id, groupGuid)
    if (response.status === 200 || response.status === 201) {
      // Remove from both list.value and contacts.value to ensure it's removed from allContacts
      list.value = list.value.filter(contact => contact.guid !== id)
      contacts.value = contacts.value.filter(contact => contact.guid !== id)
    } else {
      const { t } = useLocalization()
      throw new Error(t('mainAside.addContactError'))
    }
  }

  const groupsStore = useStoreGroups()

  const isMyContact = (contact: Contact) => {
    return !contact.imLogin && contact.groupIds.some(groupId => groupsStore.userGroupsObj[groupId])
  }

  watch(list, () => {
    list.value.forEach((contact) => {
      if (useChatMessageStore().findRoomMessages(contact.imLogin)?.length) {
        createPrivateRoom(contact.imLogin, contact)
      }
    })
  })

  const myContacts = computed<Contact[]>(() => {
    return allContacts.value?.filter(isMyContact) || []
  })

  const uploadUserContactPhoto = async (guid: string, file: File) => {
    try {
      const response = await useApi().editUserContactPhoto(guid, file)
      if (response.status === 200) {
        const contact = list.value.find(x => x.guid === guid)
        if (contact) {
          // Нужно для того, чтобы отобразился новый аватар в списке контактов
          contact.isPhotoAvailable = false
          setTimeout(() => {
            contact.isPhotoAvailable = true
          }, 0)
        }
      }
    } catch (e) {
      console.error(e)
      const { t } = useLocalization()
      throw new Error(t('mainAside.failedToLoadAvatarBecauseOfExtension'))
    }
  }

  const removeUserContactPhoto = async (guid: string) => {
    const response = await useApi().removeUserContactPhoto(guid)
    if (response.status === 200) {
      const contact = list.value.find(x => x.guid === guid)
      if (contact) {
        contact.isPhotoAvailable = false
      }
    }
  }

  const fetchContactsBySomeIds = async (ids: string | string[], fieldName: 'imLogin' | 'internalNumber' | 'mobilePhone' | 'id' = 'imLogin', forceFetch: boolean = false): Promise<Contact[]> => {
    if (list.value.length === total.value && total.value > 0) return []

    const idsForQuery: string[] = []
    // Отсеиваем те id, контакты с которыми уже есть в списке и те, по которым уже идет запрос
    if (Array.isArray(ids)) {
      ids
        .filter(id => forceFetch || (fieldName === 'internalNumber' ? !findByInternalNumber(id) : !findByPServed(id)) && !fetchingIds.has(id))
        .forEach(id => idsForQuery.push(id))
    } else if (
      typeof ids === 'string' &&
      fieldName === 'mobilePhone' ? !findContactByMobilePhone(ids) : fieldName === 'internalNumber' ? !findByInternalNumber(ids) : !findByPServed(ids) &&
      !fetchingIds.has(ids)
    ) {
      idsForQuery.push(ids)
    }
    // здесь я отредактировала поле по номеру телефона и оно работает, но надо каким-то образом совместить с внутренними контактами
    idsForQuery.forEach(id => fetchingIds.add(id))

    const MAX_VALUES_PER_QUERY = 50
    const chunks = []
    // На беке говорят, не надо слать больше 20 значений за раз
    for (let i = 0; i < idsForQuery.length; i += MAX_VALUES_PER_QUERY) {
      chunks.push([...idsForQuery].slice(i, i + MAX_VALUES_PER_QUERY))
    }

    const queries: GetContactsQuery[] = []
    chunks.forEach(chunk => {
      queries.push({
        Filters: [{
          AvailableOperations: ['eq'],
          AvailableValues: chunk.join(';'),
          PropertyName: fieldName,
          TypeFilter: fieldName === 'internalNumber' ? 'phone' : 'imLogin' ,
        }],
        Page: 1,
        Size: chunk.length,
      })
    })

    try {
      const { getContacts } = useApi()

      const promises = Promise.all(queries.map(q => getContacts(q)))
      const responses = await promises

      const contacts: Contact[] = []
      responses?.forEach((response) => {
        response.data.list?.forEach(item => {
          if (!findByPServed(item.imLogin)) {
            setContactIfDoesNotExists(item)
          }
          contacts.push(item)
        })
      })

      return contacts
    } catch (e) {
      console.error(e)
      return []
    } finally {
      idsForQuery.forEach(id => fetchingIds.delete(id))
    }
  }

  // На беке сказали присылать "большой размер"
  const MAX_SIZE = 9999999
  const fetchContactsByBirthdays = async (dateFrom: string, dateTo: string) => {
    if (list.value.length === total.value) return

    const fromYearMonth = dayjs(dateFrom).format('YYYY-MM')
    const toYearMonth = dayjs(dateTo).format('YYYY-MM')

    if (fromYearMonth === toYearMonth && alreadyFetchedMonths.has(fromYearMonth)) return

    const query: GetContactsQuery = {
      Filters: [{
        AvailableOperations: ['contains'],
        AvailableValues: [`start=${dateFrom}`, `end=${dateTo}`].join(';'),
        PropertyName: 'birthDay',
        TypeFilter: 'dateTime',
      }],
      Page: 1,
      Size: MAX_SIZE,
    }

    try {
      const { getContacts } = useApi()
      const response = await getContacts(query)

      if (fromYearMonth === toYearMonth) {
        alreadyFetchedMonths.add(fromYearMonth)
      }

      const contacts: Contact[] = []
      response.data.list.forEach(item => {
        if (!list.value.find(i => i.imLogin === item.imLogin)) {
          setContactIfDoesNotExists(item)

          contacts.push(item)
        }
      })

      return contacts
    } catch (e) {
      console.error(e)
      return
    }
  }

  const fetchContactsByNumber = (string: string): Promise<Contact[]> => {
    return new Promise((resolve, reject) => {
      const { getContacts } = useApi()

      const promises = [
        getContacts({
          Filters: [
            {
              AvailableOperations: ['eq'],
              AvailableValues: string,
              PropertyName: 'internalNumber',
              TypeFilter: 'phone',
            },
          ],
          Page: 1,
          Size: MAX_SIZE,
        }),
        getContacts({
          Filters: [
            {
              AvailableOperations: ['eq'],
              AvailableValues: string,
              PropertyName: 'mobilePhone',
              TypeFilter: 'phone',
            },
          ],
          Page: 1,
          Size: MAX_SIZE,
        })]

      Promise.all(promises)
        .then((result) => {
          const contacts: Contact[] = []
          result.flatMap(i => i.data.list).forEach(item => {
            if (!allContacts.value.find(i => i.id === item.id)) {
              setContactIfDoesNotExists(item)
            }
            contacts.push(item)
          })

          resolve(contacts)
        })
        .catch(reject)
    })
  }

  const fetchContactsByNameOrNumber = (string: string): Promise<Contact[]> => {
    return new Promise((resolve, reject) => {
      const { getContacts } = useApi()

      const promises = []
      if (/^\d+$/.test(string)) {
        promises.push(
          getContacts({
            Filters: [
              {
                AvailableOperations: ['contains'],
                AvailableValues: string,
                PropertyName: 'internalNumber',
                TypeFilter: 'phone',
              },
            ],
            Page: 1,
            Size: MAX_SIZE,
          }),
          getContacts({
            Filters: [
              {
                AvailableOperations: ['contains'],
                AvailableValues: string,
                PropertyName: 'mobilePhone',
                TypeFilter: 'phone',
              },
            ],
            Page: 1,
            Size: MAX_SIZE,
          }),
        )
      } else {
        promises.push(
          getContacts({
            Filters: [
              {
                AvailableOperations: ['contains'],
                AvailableValues: string,
                PropertyName: 'names',
                TypeFilter: 'string',
              },
            ],
            Page: 1,
            Size: MAX_SIZE,
          }),
        )
      }

      Promise.all(promises)
        .then((result) => {
          const contacts: Contact[] = []
          result.flatMap(i => i.data.list).forEach(item => {
            if (!allContacts.value.find(i => i.id === item.id)) {
              setContactIfDoesNotExists(item)
            }
            contacts.push(item)
          })

          resolve(mergeSystemBotSearchResults(string, contacts))
        })
        .catch(reject)
    })
  }

  const fetchFavoritesContacts = async () => {
    const { getContacts } = useApi()

    try {
      const { data: { list }} = await getContacts({
        Filters: [
          {
            AvailableOperations: ['eq'],
            AvailableValues: 'true',
            PropertyName: 'isFavorite',
            TypeFilter: 'string',
          },
        ],
        Page: 1,
        Size: 1000,
      })

      list.forEach(setContactIfDoesNotExists)

      return list
    } catch (e) {
      console.error(e)
      return
    }
  }

  const fetchContactsByNameOrNumberIfListIsNotLoaded = async (string: string): Promise<Contact[]> => {
    if (list.value.length === total.value) {
      return findMatchingSystemBotContacts(string)
    }

    try {
      return await fetchContactsByNameOrNumber(string)
    } catch (e) {
      console.error(e)
      return findMatchingSystemBotContacts(string)
    }
  }

  const $reset = () => {
    reset()
    listForGuestRoom.value = new Map()
    selectedGroup.value = GroupsTypes.ALL
    ownStatus.value = OwnStatus.DEFAULT
    ownStatusMessage.value = ''
    botPServedsToFetch.value = new Set()
    botPServedsFetching.clear()
    botPServedsResolved.clear()
  }

  watch(() => pServedsToFetch.value.size, throttle(async () => {
    const toFetch = Array.from([...pServedsToFetch.value])
    pServedsToFetch.value.clear()
    await fetchContactsBySomeIds(toFetch, 'imLogin')
  }, 1000))

  watch(() => internalNumbersToFetch.value.size, throttle(async () => {
    const toFetch = Array.from([...internalNumbersToFetch.value])
    internalNumbersToFetch.value.clear()
    await fetchContactsBySomeIds(toFetch, 'internalNumber')
  }, 1000))

  watch(() => botPServedsToFetch.value.size, throttle(async () => {
    const toFetch = Array.from(botPServedsToFetch.value)
    botPServedsToFetch.value = new Set()
    await Promise.all(toFetch.map(async (pServed) => {
      if (botPServedsFetching.has(pServed) || botPServedsResolved.has(pServed)) {
        return
      }

      botPServedsFetching.add(pServed)
      try {
        const name = await resolveBotDisplayName(pServed, fromPServedUser(pServed))
        applyResolvedBotIdentity(pServed, name)
      } catch (e) {
        console.error(e)
      } finally {
        botPServedsFetching.delete(pServed)
        botPServedsResolved.add(pServed)
      }
    }))
  }, 300))

  watch(currentUser, () => {
    try {
      if (currentUser.value) {
        fetchNext()
        // fetchNextExternalContacts()
        fetchFavoritesContacts()
      } else {
        $reset()
      }
    } catch {
    }
  }, { immediate: true })

  return {
    alphabeticallySortedContacts,
    allContacts,
    favoriteContacts,
    internalNumberContactMap,
    list,
    listForGuestRoom,
    ownStatus,
    ownStatusMessage,
    selectedGroup,
    findContactByMobilePhone,
    isContactCanBeAddedToPersonal,
    addContactsForGuestRoom,
    createExternalContact,
    findById,
    findByInternalNumber,
    findByPServed,
    findGuestById,
    setFavorite,
    setSelectedGroup,
    updateStatus,
    total,
    contactStoreIsReady,
    fetchNext,
    isLoading,
    myContacts,
    addUserContact,
    removeUserContact,
    uploadUserContactPhoto,
    removeUserContactPhoto,
    editUserContact,
    fetchContactsBySomeIds,
    fetchContactsByBirthdays,
    fetchContactsByNameOrNumber,
    isMyContact,
    setContactIfDoesNotExists,
    upsertBotContact,
    fetchContactsByNameOrNumberIfListIsNotLoaded,
    fetchContactsByNumber,
    findByGuid,
    contactsUserAgents: contactsUserAgents,
    $reset,
  }
})
